import React from 'react';
import { observer } from 'mobx-react-lite';
import { Button, IMFormModal, Tooltip } from '@insurancemenu/imenu-components-lib';
import { Add } from '@insurancemenu/imenu-components-lib/dist/icons';
import { DefaultFormFields } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { AlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { ReplicatedInfoDelegate, ReplicatedInfoItem } from 'store/modules/dynamic-form/replicatedInfoDelegate';
import InfoCardsTable from './InfoCardsTable';
import { InfoWrapperStyled, ButtonWrapperStyled } from './styles';

export interface ReplicatedInfoTableColumn<ItemType extends ReplicatedInfoItem = ReplicatedInfoItem> {
  title: string;
  getValue: (item: ItemType) => string;
  width?: string;
}

interface Props<ItemType extends ReplicatedInfoItem, ItemFormFields extends DefaultFormFields> {
  delegate: ReplicatedInfoDelegate<ItemType, ItemFormFields>;
  addButtonLabel: string;
  modalItemTitle: string;
  columns: ReplicatedInfoTableColumn<ItemType>[];
  addItemFormFields: React.ReactNode;
  editItemFormFields: React.ReactNode;
}

function ReplicatedInfoInner<ItemType extends ReplicatedInfoItem, ItemFormFields extends DefaultFormFields>({
  delegate,
  addButtonLabel,
  modalItemTitle,
  columns,
  addItemFormFields,
  editItemFormFields,
}: Props<ItemType, ItemFormFields>): React.ReactNode {
  const { addItemModal, editItemModal, itemsList, maxItems, addItemForm, editItemForm } = delegate;

  return (
    <InfoWrapperStyled>
      {/* Don't allow to add more info cards than a certain amount */}
      <ButtonWrapperStyled>
        {itemsList.length >= maxItems ? (
          <Tooltip alignment={AlignmentTypes.BOTTOM_START} label="You have reached the maximum number of allowed items">
            <Button leftIconProp={<Add />} color="primary" size="large" disabled>
              {addButtonLabel}
            </Button>
          </Tooltip>
        ) : (
          <Button leftIconProp={<Add />} color="primary" size="large" onClick={addItemModal.open}>
            {addButtonLabel}
          </Button>
        )}
      </ButtonWrapperStyled>
      {itemsList.length > 0 && (
        <InfoCardsTable
          items={itemsList}
          columns={columns}
          onEditItem={(itemId, item) => delegate.openForEdit(itemId, item)}
          onDeleteItem={delegate.deleteItem}
        />
      )}
      <IMFormModal
        show={addItemModal.isVisible}
        onClose={(): void => delegate.closeAddItemModal()}
        formController={addItemForm}
        title={`Add ${modalItemTitle}`}
        formId="add-item-form"
        formContent={addItemFormFields}
        submitButton={
          <Button
            color="primary"
            size="large"
            htmlType="submit"
            disabled={!addItemForm.basicFieldsValidationsPassed}
            onClick={(e): void => {
              e.stopPropagation();
              void addItemForm.handleOnSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
            }}
          >
            Save
          </Button>
        }
      />
      <IMFormModal
        show={editItemModal.isVisible}
        onClose={(): void => delegate.closeEditItemModal()}
        formController={editItemForm}
        title={`Edit ${modalItemTitle}`}
        formId="edit-item-form"
        formContent={editItemFormFields}
        submitButton={
          <Button
            color="primary"
            size="large"
            htmlType="submit"
            disabled={!editItemForm.basicFieldsValidationsPassed}
            onClick={(e): void => {
              e.stopPropagation();
              void editItemForm.handleOnSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
            }}
          >
            Save
          </Button>
        }
      />
    </InfoWrapperStyled>
  );
}

const ReplicatedInfo = observer(ReplicatedInfoInner) as typeof ReplicatedInfoInner;

export default ReplicatedInfo;
